<template>
  <div>
    <div class="tw:flex tw:items-center tw:gap-2 tw:mb-2">
      <Toggle
        :id="`${fieldName}-has-condition`"
        :width="30"
        :height="15"
        :model-value="enabled"
        :disabled="!sources.length"
        @update:model-value="toggle"
      />
      <label :for="`${fieldName}-has-condition`" class="tw:text-sm tw:font-medium">
        {{ $t('Apply a rule to this field') }}
      </label>
    </div>

    <p v-if="!sources.length" class="tw:text-sm tw:text-muted-foreground">
      {{ $t('Add a field that can drive a rule to the form first') }}
    </p>

    <template v-else-if="rule">
      <div class="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Combobox
          :options="effectOptions"
          :model-value="rule.effect"
          :placeholder="$t('Effect')"
          @update:model-value="onEffectChange"
        />
        <span class="tw:text-sm tw:text-muted-foreground">{{ $t('only when all of:') }}</span>
      </div>

      <!-- Side by side a value stays beside the operation it belongs to; stacked it
           carries its own label, since the header has nowhere to sit. -->
      <div class="tw:mb-1 tw:hidden tw:items-center tw:gap-2 tw:text-xs tw:text-muted-foreground tw:sm:flex">
        <div :class="conditionColumns">
          <span>{{ $t('Field') }}</span>
          <span>{{ $t('Condition') }}</span>
          <span>{{ $t('Value') }}</span>
        </div>
        <span class="tw:w-8 tw:shrink-0"></span>
      </div>

      <div
        v-for="(condition, index) in rule.all"
        :key="index"
        class="tw:mb-3 tw:flex tw:items-start tw:gap-2 tw:sm:mb-2 tw:sm:items-center"
        :class="index > 0 && 'tw:border-t tw:pt-3 tw:sm:border-t-0 tw:sm:pt-0'"
      >
        <div :class="conditionColumns">
          <label class="tw:flex tw:flex-col tw:gap-1 tw:min-w-0">
            <span class="tw:text-xs tw:text-muted-foreground tw:sm:hidden">{{ $t('Field') }}</span>
            <Combobox
              fullwidth
              :options="sourceOptions"
              :model-value="condition.field"
              :placeholder="$t('Field')"
              @update:model-value="onSourceChange(index, $event)"
            />
          </label>

          <label class="tw:flex tw:flex-col tw:gap-1 tw:min-w-0">
            <span class="tw:text-xs tw:text-muted-foreground tw:sm:hidden">{{ $t('Condition') }}</span>
            <Combobox
              fullwidth
              :options="operationOptions(condition.field)"
              :model-value="condition.operation"
              :placeholder="$t('Condition')"
              @update:model-value="onOperationChange(index, $event)"
            />
          </label>

          <label class="tw:flex tw:flex-col tw:gap-1 tw:min-w-0">
            <span class="tw:text-xs tw:text-muted-foreground tw:sm:hidden">{{ $t('Value') }}</span>
            <input
              v-if="isAgeOperation(condition.operation)"
              type="number"
              min="0"
              max="120"
              class="tw:w-full tw:rounded-md tw:border tw:px-2 tw:py-2 tw:text-sm"
              :value="condition.value"
              :aria-label="$t('Age in years')"
              @input="onValueChange(index, ($event.target as HTMLInputElement).value)"
            >
            <Combobox
              v-else
              fullwidth
              :options="valueOptions(condition.field)"
              :model-value="String(condition.value)"
              :placeholder="$t('Value')"
              @update:model-value="onValueChange(index, $event)"
            />
          </label>
        </div>

        <span class="tw:w-8 tw:shrink-0">
          <Button
            v-if="rule.all.length > 1"
            variant="ghost"
            size="icon"
            class="tw:text-muted-foreground"
            :aria-label="$t('Remove this condition')"
            @click="removeCondition(index)"
          >
            <Icon name="X" />
          </Button>
        </span>
      </div>

      <Button variant="ghost" size="sm" @click="addCondition">
        <Icon name="CirclePlus" />
        {{ $t('Add a condition') }}
      </Button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Toggle from '@/components/ui/Toggle.vue'
import Combobox from '@/components/ui/Combobox.vue'
import { Button } from '@/shadcn/components/ui/button'
import { Icon } from '@/components/ui/icon'
import {
  isAgeOperation,
  DEFAULT_AGE,
  type AvailableField,
  type Condition,
  type ConditionChoice,
  type FieldConditionRule,
} from '@/types/FieldConfig'

const props = defineProps<{
  fieldName: string;
  modelValue?: FieldConditionRule | null;
  /** Fields in the form that can drive a rule, excluding this one. */
  sources: AvailableField[];
  effects: ConditionChoice[];
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', rule: FieldConditionRule | null): void;
}>()

/** Stacked while the three cannot fit side by side, three equal columns once they can. */
const conditionColumns = 'tw:grid tw:flex-1 tw:min-w-0 tw:gap-2 tw:sm:grid-cols-3 tw:sm:items-center'

const rule = computed<FieldConditionRule | null>(() => props.modelValue ?? null)
const enabled = computed<boolean>(() => rule.value !== null)

const effectOptions = computed<ConditionChoice[]>(() => props.effects)

const sourceOptions = computed<ConditionChoice[]>(() =>
  props.sources.map(source => ({ value: source.name, label: source.label }))
)

const sourceFor = (name: string): AvailableField | undefined =>
  props.sources.find(source => source.name === name)

const operationOptions = (name: string): ConditionChoice[] =>
  sourceFor(name)?.condition_source?.operations ?? []

const valueOptions = (name: string): ConditionChoice[] =>
  sourceFor(name)?.condition_source?.options ?? []

/** A condition that is already valid, so the editor never emits a half-built rule. */
const conditionFor = (name: string): Condition => {
  const operation = operationOptions(name)[0]?.value ?? ''

  return {
    field: name,
    operation,
    value: isAgeOperation(operation) ? DEFAULT_AGE : (valueOptions(name)[0]?.value ?? ''),
  }
}

const emitRule = (changes: Partial<FieldConditionRule>): void => {
  emit('update:modelValue', {
    effect: rule.value?.effect ?? 'show',
    all: rule.value?.all ?? [],
    ...changes,
  })
}

const toggle = (on: boolean): void => {
  emit(
    'update:modelValue',
    on && props.sources.length
      ? { effect: 'show', all: [conditionFor(props.sources[0].name)] }
      : null
  )
}

const onEffectChange = (effect: string): void =>
  emitRule({ effect: effect as FieldConditionRule['effect'] })

const replaceAt = (index: number, condition: Condition): void =>
  emitRule({ all: rule.value!.all.map((current, i) => (i === index ? condition : current)) })

/** Changing the source invalidates the operation and the value, so both are reset. */
const onSourceChange = (index: number, name: string): void => replaceAt(index, conditionFor(name))

const onOperationChange = (index: number, operation: string): void => {
  const condition = rule.value!.all[index]
  const switchesKind = isAgeOperation(operation) !== isAgeOperation(condition.operation)

  replaceAt(index, {
    ...condition,
    operation,
    value: switchesKind
      ? (isAgeOperation(operation) ? DEFAULT_AGE : (valueOptions(condition.field)[0]?.value ?? ''))
      : condition.value,
  })
}

const onValueChange = (index: number, value: string): void =>
  replaceAt(index, { ...rule.value!.all[index], value })

const addCondition = (): void =>
  emitRule({ all: [...rule.value!.all, conditionFor(props.sources[0].name)] })

const removeCondition = (index: number): void =>
  emitRule({ all: rule.value!.all.filter((_, i) => i !== index) })
</script>
